import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Check, X } from "lucide-react";
import { adminApi, tokenStore } from "../lib/adminApi";
import { useAdminAuth } from "./AdminAuthContext";

const RULES = [
  { key: "len", label: "At least 14 characters", test: (p) => p.length >= 14 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One number", test: (p) => /\d/.test(p) },
  { key: "symbol", label: "One symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function detailToText(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
}

export default function AdminAccountPage() {
  const { admin } = useAdminAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const allRulesPass = RULES.every((r) => r.test(form.next));
  const mismatch = form.confirm.length > 0 && form.next !== form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!allRulesPass) {
      toast.error("New password doesn't meet the requirements yet.");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
        confirm_password: form.confirm,
      });
      tokenStore.set(data.access_token); // keep this session logged in
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated. Other sessions have been signed out.");
    } catch (err) {
      toast.error(detailToText(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, testId) => (
    <label className="grid gap-1.5">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input
        type="password"
        autoComplete={key === "current" ? "current-password" : "new-password"}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm"
        required
        data-testid={testId}
      />
    </label>
  );

  return (
    <div data-testid="admin-account-page" className="max-w-xl">
      <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
        Account &amp; Security
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Signed in as <span className="font-semibold text-slate-700">{admin?.email}</span>
      </p>

      <form
        onSubmit={submit}
        className="mt-6 rounded-2xl bg-white border border-slate-200 p-6 grid gap-4"
      >
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-slate-500" />
          <h2 className="font-display text-sm font-extrabold uppercase tracking-wider text-slate-900">
            Change password
          </h2>
        </div>
        {field("Current password", "current", "account-current-password")}
        {field("New password", "next", "account-new-password")}
        {field("Confirm new password", "confirm", "account-confirm-password")}
        {mismatch && (
          <p className="text-xs font-semibold text-rose-600" data-testid="account-mismatch-error">
            New passwords do not match.
          </p>
        )}
        <ul className="grid gap-1" data-testid="account-password-rules">
          {RULES.map((r) => {
            const ok = r.test(form.next);
            return (
              <li key={r.key} className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-600" : "text-slate-400"}`}>
                {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                {r.label}
              </li>
            );
          })}
        </ul>
        <button
          type="submit"
          disabled={saving}
          className="btn-primary justify-center disabled:opacity-50"
          data-testid="account-change-password-submit"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
        <p className="text-xs text-slate-400">
          Changing your password immediately signs out any other active admin
          sessions. Email verification for sensitive changes will be enabled
          once the Nexora mailbox goes live.
        </p>
      </form>
    </div>
  );
}
